import React from 'react';
import PropTypes from 'prop-types';
import { orderBy } from 'natural-orderby';
import { InfoAlert } from 'smartsuite-ui';
import WidgetErrorMessage from '../../WidgetErrorMessage';
import TimeSeriesBiaxialLineGraph from './TimeSeriesBiaxialLineGraph';
import { formatTimeSeriesDatasetMultipleSources } from '../../utils/timeSeriesDataFormatUtils';
import {
    hasMetricSources,
    getMetricSourceForRequest,
    allMetricSourcesAuthorized,
    getMetricSourceErrors,
    displayMetricSourceError,
} from '../../utils/wrapperUtils';
import {
    getUniqueArray,
    getTimeSeriesTickFormatter,
    getFormattedTimeFromMilliseconds,
} from '../../utils/dataFormatUtils';

/* eslint-disable complexity */
function getMetricSource({ metricRequests, metrics, configuration }) {
    const leftRequest = metricRequests.find(
        (request) => request.id === configuration.leftAxisMetricRequestId
    );

    const rightRequest = metricRequests.find(
        (request) => request.id === configuration.rightAxisMetricRequestId
    );

    if (
        !leftRequest ||
        leftRequest.metrics.length === 0 ||
        !rightRequest ||
        rightRequest.metrics.length === 0
    ) {
        return { errorKey: 'invalidMetricRequests' };
    }

    const leftMetricSource = getMetricSourceForRequest(metrics, leftRequest);
    const rightMetricSource = getMetricSourceForRequest(metrics, rightRequest);
    if (!leftMetricSource || !rightMetricSource) {
        return { errorKey: 'metricSourceNotFound' };
    }

    if (!allMetricSourcesAuthorized([leftMetricSource, rightMetricSource])) {
        return { errorKey: 'metricSourceUnauthorized' };
    }

    if (!hasMetricSources([leftMetricSource, rightMetricSource], false)) {
        return { errorKey: 'missingData' };
    }

    const metricSourceErrors = getMetricSourceErrors(
        [leftMetricSource, rightMetricSource],
        metricRequests
    );
    if (metricSourceErrors.length) {
        return { metricSourceErrors };
    }

    return { leftRequest, rightRequest, leftMetricSource, rightMetricSource };
}
/* eslint-enable complexity */

function getDataLabels(metricRequest, metricSource, suffix) {
    const metricValueKey = `${metricRequest.metrics[0]} ${suffix}`;
    const metricNameKey =
        metricRequest.groupBy.length === 0 ? null : metricRequest.groupBy[0];

    // prettier-ignore
    const dataLabels = !metricNameKey
        ? [metricValueKey]
        : orderBy(getUniqueArray(metricSource.metricSeries.map(
            metric => metric[metricNameKey]
        )));

    return [metricValueKey, metricNameKey, dataLabels];
}

// eslint-disable-next-line complexity
export default function TimeSeriesBiaxialLineGraphWrapper(props) {
    const {
        leftRequest,
        rightRequest,
        leftMetricSource,
        rightMetricSource,
        metricSourceErrors,
        errorKey,
    } = getMetricSource(props);

    if (errorKey) {
        return (
            <WidgetErrorMessage>
                {props.dashboardLabels[errorKey]}
            </WidgetErrorMessage>
        );
    }

    if (metricSourceErrors) {
        return metricSourceErrors.map((error) => (
            <WidgetErrorMessage key={error.uniqueRequestString}>
                {displayMetricSourceError(error, props.dashboardLabels)}
            </WidgetErrorMessage>
        ));
    }

    const leftDataFormatter = props.configuration.isLeftTime
        ? getFormattedTimeFromMilliseconds
        : undefined;

    const rightDataFormatter = props.configuration.isRightTime
        ? getFormattedTimeFromMilliseconds
        : undefined;

    const [leftMetricValueKey, leftMetricNameKey, leftDataLabels] =
        getDataLabels(leftRequest, leftMetricSource, '(left)');

    const [rightMetricValueKey, rightMetricNameKey, rightDataLabels] =
        getDataLabels(rightRequest, rightMetricSource, '(right)');

    const formattedData = formatTimeSeriesDatasetMultipleSources(
        leftMetricSource,
        rightMetricSource,
        leftDataLabels,
        rightDataLabels,
        leftMetricNameKey,
        leftMetricValueKey,
        rightMetricNameKey,
        rightMetricValueKey,
        props.numberDecimalFormatter,
        props.configuration.disconnectedGraph
    );

    return (
        <>
            {leftMetricSource.isTruncated && (
                <InfoAlert>
                    {`${props.dashboardLabels.truncationWarning} ${leftRequest.truncationThreshold}`}
                </InfoAlert>
            )}
            <div className="chart-container">
                <TimeSeriesBiaxialLineGraph
                    {...props}
                    bucketTimes={leftMetricSource.bucketTimes}
                    chartProperties={props.configuration}
                    dataPoints={formattedData}
                    dateFormatter={getTimeSeriesTickFormatter(
                        leftRequest.timeAndPrecision.precisionType
                    )}
                    leftDataFormatter={leftDataFormatter}
                    leftDataLabels={leftDataLabels}
                    rightDataFormatter={rightDataFormatter}
                    rightDataLabels={rightDataLabels}
                />
            </div>
        </>
    );
}

TimeSeriesBiaxialLineGraphWrapper.propTypes = {
    configuration: PropTypes.instanceOf(Object).isRequired,
    dashboardLabels: PropTypes.instanceOf(Object).isRequired,
    numberDecimalFormatter: PropTypes.func.isRequired,
};
